import React, { useId, useState } from 'react';

import { cssVars } from './css-vars';
import { dateShort2 } from '../../../assets/contants/dates-fns';
import { formatPlainDate, plainDateFromEpochDay } from '../../../core/services/plain-date';
import { isDragDisabledFor, modeForHit } from '../interaction/drag-math';
import { useBarDrag } from '../interaction/use-bar-drag';
import { useBarKeyboard } from '../interaction/use-bar-keyboard';
import { useSpanPreview } from '../interaction/use-span-preview';
import { durationOf, formatDurationEnglish, totalDaysOf } from '../model/duration';
import { createTimeScale } from '../model/time-scale';
import styles from './bar.module.scss';

import type { BarRendererProps, Span } from '../types';

// Only reached by a `<Bar>` rendered without a live chart around it — this file's own pre-drag
// unit tests do exactly that, passing `state` directly and never simulating a gesture. No drag
// ever fires without a `scale` prop actually present, so which zoom this defaults to is inert.
const FALLBACK_SCALE = createTimeScale('day');

/**
 * Task 23. The non-standard keyboard contract `useBarKeyboard` implements, said out loud, because
 * nothing about `role="application"` tells a screen-reader user WHICH keys this widget listens
 * for — that is the price of a role whose whole meaning is "the author defines the keys". Wired
 * through `aria-describedby`, so it is spoken on focus and never appears in the browse-mode text.
 *
 * FIX 1 — WHY `Escape` IS NO LONGER PROMISED FLATLY. The first version of this string ended
 * "Enter saves, Escape cancels", which is true for a sighted keyboard user and probably false for
 * the exact users this hint exists for. NVDA's browse-mode interceptor binds `escape` to
 * `script_disablePassThrough`, which forwards the gesture to the page ONLY when focus mode was
 * entered manually (`disableAutoPassThrough`). Under the AUTOMATIC focus mode `role="application"`
 * triggers — the whole mechanism this design rests on — `Escape` turns focus mode back off and
 * never reaches `keyboard.onKeyDown`. JAWS is the same: Esc is the documented key to leave Forms
 * Mode. Worse, `useBarKeyboard`'s `onBlur` cancel does not rescue them, because a browse-mode
 * switch is not necessarily a DOM blur — the user lands back in browse mode with an uncommitted
 * preview still drawn.
 *
 * THE SECOND CANCEL PATH IS `Tab`, AND IT ALREADY EXISTS. Moving focus off the bar fires a real
 * DOM `blur`, which `handleBlur` below already routes into `keyboard.onBlur()` — the same
 * discard `Escape` performs. `Tab` is passed to the page by both readers in focus mode AND in
 * browse mode (neither intercepts it; it is how you leave any widget), so it is the one discard
 * that survives whichever mode the user is actually in. It needed naming, not building.
 *
 * NO NEW KEY WAS ADDED, and this is a deliberate outcome rather than an omission. Every candidate
 * checked with the collision rigour `use-bar-keyboard.ts` applies to its own modifiers:
 * - `Ctrl+Z`/`Cmd+Z`: passes through focus mode cleanly, but means "undo" — there is no undo
 *   stack here, only a discard of an uncommitted preview, and it would need two platform
 *   bindings. Binding a well-known verb to a different action is the mistake the `gridcell`
 *   rejection below is about.
 * - `Backspace`: Firefox still ships `browser.backspace_action` (history-back when set to 0), and
 *   NVDA/JAWS browse mode uses it for "back". Not clean.
 * - `Delete`: passes through, but reads as "delete this item" on a surface that edits production
 *   dates. Not a key to be ambiguous about.
 * - `Shift+Escape`: Chrome's task-manager shortcut on Windows/Linux. `Ctrl+Escape` is the Windows
 *   Start menu; `Alt+Escape` is Windows window-cycling. All intercepted above the page.
 * - `Space`: not a discard in any convention, and it is the page-scroll key in browse mode.
 * So: no safe SECOND KEY exists. The safe second PATH does, and the hint names it.
 *
 * `aria-keyshortcuts` (below) carries the same bindings machine-readably, because delivery of this
 * description is itself a reader SETTING — JAWS "speak descriptions" and NVDA's object-description
 * reporting can both be off, and with them off the user has an `application` and no idea what keys
 * it takes.
 *
 * English string literal rather than an `i18n.t` key, matching `empty-row-add.tsx`'s
 * `Add dates for …` and `offscreen-indicator.tsx`'s `Scroll to …` — every author-supplied
 * accessible string in this module is currently untranslated, and inventing a second convention
 * for this one would hide the gap rather than fix it. See the task report's concerns.
 */
const KEYBOARD_HINT =
  'Arrow keys move this bar by one day. Shift plus arrow keys resize the end date. ' +
  'Shift plus Alt plus arrow keys resize the start date. Enter saves. Tab away to discard; ' +
  'Escape also discards, unless your screen reader intercepts it.';

/**
 * Task 23 fix 1. The same contract, machine-readable, as the hedge against `aria-describedby`
 * being suppressed by a verbosity setting (see `KEYBOARD_HINT`). Names the bindings the ELEMENT
 * implements — which is what this attribute is for — so `Escape` belongs here even though the
 * prose above has to caveat whether it arrives: the caveat is about the reader's delivery, not
 * about what `useBarKeyboard` binds. `Tab` is deliberately absent: it is not a shortcut this
 * element binds, it is the browser moving focus, and listing it would be a false claim of a
 * handler.
 *
 * Key names are `KeyboardEvent.key` values with `+`-joined modifiers, per WAI-ARIA.
 */
const KEY_SHORTCUTS =
  'ArrowLeft ArrowRight Shift+ArrowLeft Shift+ArrowRight ' +
  'Alt+Shift+ArrowLeft Alt+Shift+ArrowRight Enter Escape';

/** `Aug 10 to Aug 12`, or just `Aug 10` when the span is a single day. Spoken, so `to` rather
 * than the `-` `offscreen-indicator.tsx` draws visually. */
const spanLabelOf = (span: Span): string => {
  const start = formatPlainDate(plainDateFromEpochDay(span.startDay), dateShort2);
  const end = formatPlainDate(plainDateFromEpochDay(span.endDay), dateShort2);

  return span.startDay === span.endDay ? start : `${start} to ${end}`;
};

/**
 * The DEFAULT bar takes exactly the published renderer seam and nothing more — this is an ALIAS of
 * `BarRendererProps`, not a parallel declaration of the same fields.
 *
 * I1. It used to be a parallel declaration, and it drifted: Tasks 18, 20 and 22 each added a prop
 * here and at `row-list.tsx`'s call site while `GanttRenderers['bar']` stayed on Task 12's
 * `{ row, state }`. Two real `tsc` errors sat unseen behind a broken `nx typecheck`, and a custom
 * renderer could not obtain the `TimeScale` its own `useBarDrag` call needs. Aliasing makes the
 * next such addition a one-place change by construction: widen `BarRendererProps` and both the
 * default component and the seam move together, or neither compiles. See `types.ts`.
 *
 * OWNERSHIP DECISION behind `state`: `Bar` — not `row-list.tsx` — owns the `useBarDrag` call below
 * and is the single source of truth for `isDragging`/`isResizing`/`previewSpan` on its OWN
 * rendered DOM (see `data-dragging`/`data-resizing` and `previewStyle`, which all read the shared
 * preview store and `drag`, never `state`, for those three fields). `RowList` cannot own the hook
 * itself: it renders a virtualized, variable-length set of rows, and calling a hook once per row
 * inside `rows.map()` would vary the hook count across renders — a Rules-of-Hooks violation. So
 * the per-row component that mounts/unmounts with the row (`Bar`) is the only place a live gesture
 * CAN be tracked.
 *
 * Consequence, stated plainly: `state` stays real and required only so `Bar`'s signature is
 * assignable to `GanttRenderers['bar']`, and it still supplies a `previewSpan`/`isFocused`
 * FALLBACK for a direct, hook-less render (this file's own pre-drag unit tests do exactly that). A
 * CUSTOM `renderers.bar` substitute does not automatically get live drag OR focus state under this
 * model — it would need to call `useBarDrag`/`useBarKeyboard` itself, the same way `Bar` does, to
 * be interactive. That is a known scope limit of Task 18, extended identically by Task 19 for
 * `isFocused` rather than inventing a second ownership model for it. What I1 changed is that the
 * seam now actually HANDS a custom renderer the `scale` those hooks require, so that sentence
 * describes something buildable rather than something merely permitted.
 *
 * Task 19: `isFocused` follows the exact same pattern as `previewSpan` — `Bar` tracks it as real
 * local state (`onFocus`/`onBlur` on its own DOM node), OR'd with `state.isFocused` so the
 * hook-less unit-test render path can still force it `true` directly.
 */
export type BarProps = BarRendererProps;

export const Bar: React.FC<BarProps> = ({ row, state, scale, readOnly, onCommit }) => {
  // One eligibility rule, read by both hooks, so pointer and keyboard interaction can never drift
  // on who is allowed to edit a row — see `isDragDisabledFor`'s own doc-comment.
  const disabled = isDragDisabledFor(row, readOnly);

  // C2. ONE preview store, shared by both gestures, because `Bar` is the one component that runs
  // both. Two independent previews merged with `??` at the render site is what let a pointer drag
  // measure from the item's real dates while the bar drew a keyboard preview, and let the stale
  // keyboard preview reappear the instant the drag's own cleared — see `use-span-preview.ts` for
  // the full three-defect reproduction. Sharing the store makes the second preview
  // unrepresentable rather than merely cleared at one of the two sites.
  const preview = useSpanPreview();

  const drag = useBarDrag({
    span: row.span,
    item: row.item,
    scale: scale ?? FALLBACK_SCALE,
    disabled,
    onCommit,
    preview,
  });
  // Refused, not cancelled, while a pointer gesture is live: a drag preview never outlives its
  // gesture, so the only overlap is with the button held down, and the mid-drag cancel that
  // belongs to that state is `useBarDrag`'s own `window` `Escape` listener. The reverse direction
  // — a pointerdown landing on a pending keyboard preview — is handled inside `useBarDrag`, which
  // adopts the drawn span as the gesture's origin. See `use-bar-keyboard.ts`'s doc-comment.
  const keyboard = useBarKeyboard({
    span: row.span,
    item: row.item,
    disabled,
    onCommit,
    preview,
    suspended: drag.mode !== null,
  });

  // Real local focus state, owned by `Bar` for the identical Rules-of-Hooks reason `useBarDrag`'s
  // live state is: `row-list.tsx` cannot call a hook once per row inside its virtualized
  // `rows.map()`. `state.isFocused` (always `false` from that static baseline in the real chart)
  // is kept as an OR'd fallback purely so a direct, hook-less `<Bar row state={{isFocused: true}}
  // />` unit-test render can still force the focused visual without simulating a real DOM focus.
  const [isFocused, setIsFocused] = useState(false);
  const focused = isFocused || state.isFocused;
  const hintId = useId();

  // Read off the shared store directly, never `drag.previewSpan ?? keyboard.previewSpan` — those
  // two now return the same value, and re-merging them would reintroduce the impression that there
  // are two sources here. `state.previewSpan` remains the fallback for a direct, hook-less
  // `<Bar row state />` render (this file's own pre-drag unit tests).
  const previewSpan = preview.span ?? state.previewSpan;
  const span = previewSpan ?? row.span;

  if (!span) return null;

  // Task 23. Built from `span` — the PREVIEW span mid-interaction, the item's own dates otherwise
  // — for the same reason `previewStyle` below is: the accessible name has to describe the bar as
  // currently drawn, or a keyboard user who has moved a bar three days hears its old dates. This
  // is the information a sighted user reads off the bar's horizontal position and width, which
  // has no non-visual equivalent otherwise.
  // FIX 1: `formatDurationEnglish`, not the i18n'd `formatDuration`. The rest of this sentence
  // (date-fns `MMM dd`, and the `, ` / ` to ` glue) is hardcoded English with no locale path
  // available, so the localised half produced ONE accessible name in TWO languages under one
  // `lang` — `Leaf, Aug 10 to Aug 12, 3 jours`. Uniformly English is the honest shape until a real
  // i18n pass can localise the whole template; see `formatDurationEnglish`'s own comment for the
  // full reasoning and for why this is latent rather than live today.
  const spanLabel = spanLabelOf(span);
  const durationLabel = formatDurationEnglish(durationOf(span));
  const accessibleName = `${row.item.name}, ${spanLabel}, ${durationLabel}`;

  // ADDS TO the offset `row-list.tsx` wrote on the ancestor `.track` from the item's own,
  // un-dragged span, rather than overriding it: `Bar` deliberately never learns `windowStartDay`,
  // so all it can contribute is the day DELTA the interaction introduced, and `bar.module.scss`
  // sums the two — `calc((var(--bar-offset) + var(--bar-offset-delta, 0)) * ...)`. This is what
  // makes the bar's rendered position "always a pure function of `dragPreview`" (spec §8) instead
  // of only its narrow/fade classification.
  //
  // A SEPARATE PROPERTY, and the reason is the user-reported "the bar disappears when I drag it".
  // This used to write `'--bar-offset': calc(var(--bar-offset) + delta)` and claim the `calc` read
  // the INHERITED `.track` value. It does not. The declaration is on the bar, so the `var()` inside
  // it resolves against the bar's OWN declaration of the same property — a self-reference, which
  // CSS Variables §3.2 makes a cycle, and every custom property in a cycle computes to the
  // guaranteed-invalid value regardless of what any ancestor holds. `left` then became invalid at
  // computed-value time and fell back to `auto`, parking the absolutely-positioned bar at the
  // track's static origin, thousands of pixels left of the viewport. Resize was hit too: its delta
  // is `0`, and `calc(var(--bar-offset) + 0)` is just as much a self-reference as `+ 2`. Writing a
  // DIFFERENT property makes the cycle unrepresentable rather than merely avoided — see
  // `bar.module.scss` for the measured numbers.
  //
  // `--bar-span` was never part of the bug and is unchanged: `totalDaysOf(previewSpan)` is a plain
  // number, so overriding the track's value with it is an ordinary override, not a cycle.
  const previewStyle =
    previewSpan && row.span
      ? cssVars({
          '--bar-offset-delta': previewSpan.startDay - row.span.startDay,
          '--bar-span': totalDaysOf(previewSpan),
        })
      : undefined;

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>): void => {
    const rect = event.currentTarget.getBoundingClientRect();
    drag.onPointerDown(modeForHit(event.clientX, rect))(event);
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    // A stuck, uncommitted keyboard preview left showing after focus moves away is a dead end for
    // a keyboard-only user (Escape can't reach it without focus) — see `useBarKeyboard.onBlur`.
    keyboard.onBlur();
  };

  return (
    <div
      data-testid="bar"
      className={styles['bar']}
      data-done={!!row.item.isDone}
      data-fade-start={!row.item.startDate}
      data-fade-end={!row.item.dueDate}
      data-narrow={totalDaysOf(span) <= 1}
      // Deliberately move-only, not `mode !== null`: the legacy chart's `isDragging` came from
      // dnd-kit's `useDraggable`, which only ever fired for a move gesture — resize there was a
      // wholly separate mousedown mechanism with no `isDragging` of its own. `data-resizing` below
      // is what carries the resize half; do not widen this to cover it too.
      data-dragging={drag.mode === 'move'}
      data-resizing={
        drag.mode === 'resize-start' ? 'start' : drag.mode === 'resize-end' ? 'end' : false
      }
      data-focused={focused}
      /*
       * TASK 23 ROLE DECISION. Until now this was a `tabIndex=0` div with NO role, which is the
       * one shape where NVDA and JAWS stay in browse mode and consume the arrow keys themselves
       * for document navigation — so Task 19's keyboard path existed for sighted keyboard users
       * and provably not for the AT users it was meant to serve.
       *
       * `application` is the role whose definition is literally this widget: "a structure
       * containing one or more focusable elements requiring user input … that do not follow a
       * standard interaction pattern supported by a widget role".
       *
       * FIX 1 — THE MECHANISM, CORRECTED. An earlier version of this comment and of the task
       * report said `application` is in NVDA's `browseMode.shouldPassThrough` role set. It is
       * NOT: that tuple is roughly COMBOBOX, EDITABLETEXT, LIST, LISTITEM, SLIDER, TABCONTROL,
       * MENUBAR, POPUPMENU, TREEVIEW, TREEVIEWITEM, SPINBUTTON, TABLEROW, TABLECELL,
       * TABLEROWHEADER, TABLECOLUMNHEADER, plus the EDITABLE state — which is why the `gridcell`
       * analysis below can cite `tablecell` from it correctly. `application` reaches focus mode by
       * a DIFFERENT path: content inside a `role="application"` subtree is not rendered into
       * NVDA's virtual buffer the way ordinary document content is, so there is no browse-mode
       * representation of it to navigate and the reader passes keystrokes straight to the page on
       * focus. JAWS's auto-forms-mode rules treat `application` the same way. The conclusion (a
       * raw `ArrowRight` reaches `keyboard.onKeyDown`) is unchanged; the evidence for it was
       * wrong and is now right.
       *
       * Necessary, not sufficient: automatic focus mode is a user-configurable SETTING in both
       * readers, so a user who has turned it off gets none of this from the role alone.
       *
       * Scoped to this one leaf element — never the chart, never the grid — so browse mode keeps
       * working everywhere else.
       *
       * Rejected, with reasons (fuller account in the Task 23 report):
       * - `button`: names the bar well, but `button` is NOT in either reader's focus-mode role
       *   set, so arrows stay eaten — it fixes discoverability and leaves the actual defect. It
       *   also promises Space-activates, which this bar does not do.
       * - Moving `tabIndex` onto the `gridcell` (`row-list.tsx`'s `.track`) and letting the grid
       *   own arrows: buildable (a per-row COMPONENT may call hooks; only a hook inside
       *   `rows.map()` is forbidden), and it does reach focus mode. Rejected because a focused
       *   `gridcell` inside a `grid` announces the grid pattern, whose contract is that arrows
       *   MOVE BETWEEN CELLS. Binding a data mutation to the exact key the announced pattern
       *   promises is navigation is worse than no keyboard path at all.
       * - `spinbutton`, and a SINGLE `slider`: model one value, so neither can express a span's
       *   two independently movable edges.
       * - The APG MULTI-THUMB SLIDER (two focusable `role="slider"` thumbs, each with its own
       *   `aria-valuemin`/`max`/`now` and an `aria-valuetext` carrying the spoken date) is NOT
       *   ruled out, and an earlier version of this comment wrongly implied it was by arguing
       *   against the single-value pattern. It is in fact stronger than `application` on three
       *   counts: `slider` IS in NVDA's pass-through tuple (`application` is not — see above);
       *   arrow keys become the NATIVE expected interaction, so no author-defined key contract and
       *   no 30-word hint are needed; and announcements come free from `valuetext`, so no live
       *   region. `aria-valuenow` need not lie either — an epoch day is a legitimate `valuenow`
       *   precisely when `valuetext` carries the human form. Its one real gap is whole-bar MOVE,
       *   which has no multi-thumb analogue. It is recorded in the task report as the LEADING
       *   Phase 3 candidate if the NVDA check on `application` comes back bad, not as a rejection.
       * - Likewise not considered before and recorded as the SECOND Phase 3 candidate: a real
       *   `<button aria-haspopup="dialog">` opening a date-entry `role="dialog"`. A dialog forces
       *   focus mode, native `<input>`s work in every reader with zero custom key contract, and it
       *   is WCAG-clean without any of the caveats on this page.
       *
       * The Task 12/15 grid roles are untouched by this: `application` is ordinary content inside
       * the `gridcell`, which keeps its row, its `aria-rowindex` and the grid's `aria-rowcount`
       * exactly as they were. `gantt-chart.test.tsx` asserts that containment directly.
       *
       * When the bar is not editable there IS no author-defined keyboard interaction, so claiming
       * `application` would be a lie: it degrades to `img`, the role for a graphic that conveys its
       * meaning as a single labelled unit.
       *
       * FIX 1: it KEEPS its tab stop (see `tabIndex` below). Note also that `img` is
       * children-presentational, so the `<span class="title">` text node below is pruned from the
       * accessibility tree on a read-only bar — the `aria-label` is the only thing left carrying
       * the name, which is fine here because that label STARTS with the visible title, so WCAG
       * 2.5.3 Label in Name holds.
       */
      role={disabled ? 'img' : 'application'}
      // "application"/"graphic" are accurate and useless to a listener. Neither reader derives
      // focus mode from this string — it is presentation only — so renaming what is spoken costs
      // nothing behavioural.
      aria-roledescription="timeline bar"
      aria-label={accessibleName}
      aria-describedby={disabled ? undefined : hintId}
      // FIX 1. Belt to `aria-describedby`'s braces: whether a description is SPOKEN is a reader
      // verbosity setting (JAWS "speak descriptions", NVDA object-description reporting), and with
      // it off the entire explanation of a non-standard key contract disappears. See
      // `KEY_SHORTCUTS`.
      aria-keyshortcuts={disabled ? undefined : KEY_SHORTCUTS}
      // Spec §8: bars are focusable. `0` rather than a roving `-1` managed across the row list:
      // a bar joins the page's normal tab order, which is the only thing that works without the
      // roving-tabindex manager this chart doesn't have.
      //
      // FIX 1 — UNCONDITIONAL AGAIN. Task 23 briefly made this `disabled ? -1 : 0` on the argument
      // that a bar answering no key is a dead stop. That was wrong in the direction opposite to
      // this task's purpose: a tab stop is also how a keyboard-only user establishes WHERE THEY
      // ARE in a wide horizontally-scrolled timeline, and focusing a bar scrolls it into view. In
      // a chart with `GanttConfig.readOnly` set, `isDragDisabledFor` is true for EVERY row, so the
      // conditional left the entire timeline pane with no focusable element at all — leaving
      // keyboard scrolling to the focusable-scroll-container behaviour that is Chrome 127+ only
      // and absent in Safari and Firefox. `role="img"` with `tabIndex={0}` — a focusable graphic —
      // is mildly unusual and loses nothing: it is not announced as interactive, it just stays
      // reachable and readable. So the ROLE varies with editability; the TAB STOP does not.
      tabIndex={0}
      style={previewStyle}
      onPointerDown={handlePointerDown}
      onKeyDown={keyboard.onKeyDown}
      onFocus={() => setIsFocused(true)}
      onBlur={handleBlur}
    >
      <span className={styles['title']}>{row.item.name}</span>

      {disabled ? null : (
        <>
          <span id={hintId} className={styles['srOnly']}>
            {KEYBOARD_HINT}
          </span>

          {/* Task 23. The keyboard path mutates a date silently otherwise: `aria-label` above
              does change with the preview, but a changed name on an ALREADY-focused element is
              not re-announced by any reader. A live region is, so this is the only thing that
              tells a listener what `ArrowRight` just did.
              Always mounted, empty between interactions, rather than conditionally rendered — a
              live region inserted in the same tick as its first content is routinely missed. */}
          <span role="status" aria-atomic="true" className={styles['srOnly']}>
            {previewSpan ? `${spanLabel}, ${durationLabel}` : ''}
          </span>
        </>
      )}
    </div>
  );
};
